import Alpine from "alpinejs";
import { alpineData } from "../../shared/alpine-data";

export interface PromptVariable {
  name: string;
  description: string;
}

export interface PromptDefinition {
  id: string;
  name: string;
  description: string;
  promptCategory: string;
  variables: PromptVariable[];
}

export interface PromptMacro {
  id: string;
  name: string;
  description: string;
  content: string;
  prompt_scope: string | null;
  is_system: boolean;
}

export interface LLMModelConfig {
  id: string;
  name: string;
  provider: string;
  is_reasoning: boolean;
}

export interface EffortOption {
  value: string;
  label: string;
}

export interface ModelEffortInfo {
  levels: EffortOption[];
  default: string;
}

export const PromptEditor = (
  initialModel: string,
  initialReasoningEffort: string,
  models: LLMModelConfig[],
  effortByModel: Record<string, ModelEffortInfo>,
  knownPrompts: PromptDefinition[],
  macros: PromptMacro[],
  initialPromptName: string,
  initialCategory: string,
) => alpineData({
  loading: false,
  modelName: initialModel,
  reasoningEffort: initialReasoningEffort,
  models: models,
  effortByModel: effortByModel,
  knownPrompts: knownPrompts,
  macros: macros,
  selectedPromptID: initialPromptName,
  promptCategory: initialCategory,

  // effortOptions lists the translated {value,label} options the currently selected
  // model accepts, sourced from the server-resolved effortByModel map (SSOT:
  // domain.EffortByModel). Empty for non-reasoning/unknown models.
  get effortOptions(): EffortOption[] {
    const info = this.effortByModel[this.modelName];
    return info ? info.levels : [];
  },

  init() {
    this.$watch("selectedPromptID", (val: string) => {
      if (this.activeDef) {
        this.promptCategory = this.activeDef.promptCategory;
      }
    });
    // Switching models can invalidate the current effort value (e.g. a level the new
    // model doesn't support). "" (use model default) is always valid and left alone;
    // an unsupported concrete value falls back to "" rather than a guessed default.
    this.$watch("modelName", (val: string) => {
      if (this.reasoningEffort === "") return;
      const info = this.effortByModel[val];
      if (!info || !info.levels.some((o) => o.value === this.reasoningEffort)) {
        this.reasoningEffort = "";
      }
    });
  },

  get activeDef(): PromptDefinition | null {
    return (this.knownPrompts || []).find((p) => p.id === this.selectedPromptID) || null;
  },

  get isReasoning(): boolean {
    const m = this.models.find((m) => m.id === this.modelName);
    return m ? m.is_reasoning : false;
  },

  insertVar(v: string) {
    const varStr = "{{." + v + "}}";
    this._insertIntoActiveEditor(varStr);
  },

  insertMacro(m: string) {
    this._insertIntoActiveEditor(m);
  },

  _insertIntoActiveEditor(text: string) {
    const el = (this as any).$el as HTMLElement;
    const editors = el.querySelectorAll('[x-data*="markdownEditor"]');
    for (const editorEl of editors) {
      const component = (window as any).Alpine.$data(editorEl);
      if (component?._editorView?.hasFocus) {
        component.insertAtCursor(text);
        return;
      }
    }
    // Fallback: insert into first editor
    if (editors.length > 0) {
      const component = (window as any).Alpine.$data(editors[0]);
      component?.insertAtCursor(text);
    }
  },
});
